import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { boreholes as api, intakes as intakesApi, type BoreholeSummary } from '@/api/endpoints';
import type { Intake } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { normKey } from '@/lib/duplicates';

/** Etykieta i kolor pigułki funkcji otworu. */
const FUNC_LABEL: Record<string, string> = {
  PRODUCTION: 'Studnia',
  OBSERVATION: 'Piezometr',
  REPLACEMENT: 'Zastępczy',
  INACTIVE: 'Nieczynny',
};
const FUNC_PILL: Record<string, string> = {
  PRODUCTION: 'pill-water',
  OBSERVATION: 'pill-gray',
  REPLACEMENT: 'pill-earth',
  INACTIVE: 'pill-gray',
};
const FUNC_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Wszystkie' },
  { value: 'PRODUCTION', label: 'Studnie' },
  { value: 'OBSERVATION', label: 'Piezometry' },
  { value: 'REPLACEMENT', label: 'Zastępcze' },
  { value: 'INACTIVE', label: 'Nieczynne' },
];

/**
 * „Karty otworów" — globalna lista wszystkich otworów (wszystkie ujęcia) z dostępem do
 * edytora karty. Kartę można też stworzyć z tego poziomu: kreator wybiera ujęcie i otwór
 * (istniejący lub nowy) i przenosi do edytora. Analogia do modułu Analiza sitowa.
 */
export function BoreholeCardsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<BoreholeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [funcFilter, setFuncFilter] = useState('');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);

  const reload = () => {
    setLoading(true);
    api.listAll()
      .then(setItems)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter(b => {
      if (funcFilter && b.function !== funcFilter) return false;
      if (!needle) return true;
      return [b.designation, b.intakeName, b.clientName, b.town, b.voivodeship]
        .some(v => (v ?? '').toLowerCase().includes(needle));
    });
  }, [items, funcFilter, q]);

  const openCard = (id: number) =>
    navigate('/otwory/' + id + '/karta', { state: { backLabel: 'Karty otworów' } });

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Karty otworów</h1>
          <div className="text-ink-400 text-[13px]">
            Wszystkie otwory i ich karty — zestawienie wyników wiercenia, projekt, likwidacja, rekonstrukcja
          </div>
        </div>
        <button onClick={() => setShowNew(true)} className="btn btn-primary btn-sm">
          <Icon name="plus" className="w-3.5 h-3.5" /> Nowa karta otworu
        </button>
      </header>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {FUNC_FILTERS.map(f => (
          <button key={f.value} onClick={() => setFuncFilter(f.value)}
            className={'btn btn-sm ' + (funcFilter === f.value ? 'btn-primary' : 'btn-ghost')}>
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 bg-ink-50 border border-ink-100 px-2.5 py-1.5 rounded-md text-ink-400 focus-within:border-water-500 focus-within:bg-white">
          <Icon name="search" className="w-3.5 h-3.5" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Szukaj otworu, ujęcia, klienta…"
            className="border-0 bg-transparent outline-none text-[13px] text-ink-800 w-[220px]" />
        </div>
      </div>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}
      {loading && <div className="text-ink-400">Ładowanie…</div>}

      {!loading && items.length === 0 && (
        <div className="card"><div className="card-body text-center text-ink-400 py-8">
          Brak otworów. Kliknij <strong>Nowa karta otworu</strong>, aby wybrać ujęcie i otwór albo utworzyć nowy.
        </div></div>
      )}

      {!loading && items.length > 0 && filtered.length === 0 && (
        <div className="card"><div className="card-body text-center text-ink-400 py-8">
          Brak otworów pasujących do filtra.
        </div></div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="card">
          <div className="card-body !p-0">
            <table className="table-hw">
              <thead>
                <tr>
                  <th className="w-28">Otwór</th>
                  <th>Ujęcie / klient</th>
                  <th>Lokalizacja</th>
                  <th className="w-24 text-right">Głębokość</th>
                  <th className="w-28">Funkcja</th>
                  <th className="w-28">Karta</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => (
                  <tr key={b.id} onClick={() => openCard(b.id)}>
                    <td className="font-mono font-semibold text-ink-900">{b.designation}</td>
                    <td>
                      <div className="font-semibold text-ink-800">{b.intakeName || '—'}</div>
                      {b.clientName && <div className="text-[11px] text-ink-400">{b.clientName}</div>}
                    </td>
                    <td className="text-ink-500 text-[12px]">
                      {[b.town, b.voivodeship].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="text-right font-mono tabular-nums">
                      {b.totalDepth != null ? `${b.totalDepth} m` : '—'}
                    </td>
                    <td>
                      <span className={'pill ' + (FUNC_PILL[b.function] || 'pill-gray')}>
                        {FUNC_LABEL[b.function] || b.funkcjaName}
                      </span>
                    </td>
                    <td>
                      {b.hasCard
                        ? <span className="pill pill-green"><Icon name="check" className="w-3 h-3" /> Zapisana</span>
                        : <span className="text-ink-400 text-[12px]">— szkic</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showNew && (
        <NewBoreholeCardModal
          onClose={() => setShowNew(false)}
          onGo={(id) => openCard(id)}
        />
      )}
    </div>
  );
}

const NEW_FUNCTIONS: Array<{ value: string; label: string }> = [
  { value: 'PRODUCTION', label: 'Studnia (eksploatacyjny)' },
  { value: 'OBSERVATION', label: 'Piezometr (obserwacyjny)' },
  { value: 'REPLACEMENT', label: 'Zastępczy' },
  { value: 'INACTIVE', label: 'Nieczynny' },
];

/**
 * Kreator „Nowa karta otworu": 1) wybór ujęcia (wymagany — otwór musi należeć do ujęcia),
 * 2) wskazanie istniejącego otworu albo utworzenie nowego (oznaczenie + funkcja) → edytor karty.
 */
function NewBoreholeCardModal({ onClose, onGo }: { onClose: () => void; onGo: (boreholeId: number) => void }) {
  const [intakeList, setIntakeList] = useState<Intake[]>([]);
  const [intakeId, setIntakeId] = useState<number | ''>('');
  const [mode, setMode] = useState<'existing' | 'new'>('new');
  const [existingList, setExistingList] = useState<{ id: number; designation: string; function: string }[]>([]);
  const [existingId, setExistingId] = useState<number | ''>('');
  const [designation, setDesignation] = useState('');
  const [func, setFunc] = useState('PRODUCTION');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { intakesApi.list().then(setIntakeList).catch(() => {}); }, []);

  // Otwór o tym oznaczeniu, który w wybranym ujęciu już jest. Lista `existingList` i tak
  // jest pobierana na potrzeby trybu „Istniejący", więc kontrola nie kosztuje żądania.
  const kolidujacy = useMemo(
    () => existingList.find(b => normKey(b.designation) === normKey(designation)) ?? null,
    [existingList, designation]);

  // Po zmianie ujęcia pobierz jego otwory (do trybu „istniejący").
  useEffect(() => {
    setExistingId('');
    if (intakeId === '') { setExistingList([]); return; }
    api.listForIntake(Number(intakeId)).then(setExistingList).catch(() => setExistingList([]));
  }, [intakeId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (intakeId === '') { setErr('Wybierz ujęcie'); return; }
    setBusy(true); setErr(null);
    try {
      if (mode === 'existing') {
        if (existingId === '') { setErr('Wybierz otwór'); setBusy(false); return; }
        onGo(Number(existingId));
        return;
      }
      if (!designation.trim()) { setErr('Podaj oznaczenie otworu'); setBusy(false); return; }
      if (kolidujacy) {
        setErr(`To ujęcie ma już otwór „${kolidujacy.designation}". Wybierz go w trybie „Istniejący" `
             + 'zamiast zakładać drugi o tym samym oznaczeniu.');
        setBusy(false);
        return;
      }
      const created = await api.create(Number(intakeId), { designation: designation.trim(), function: func });
      onGo(created.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { intakeId, mode, existingId, designation, func }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[460px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100 flex items-center">
          <h2 className="text-[15px] font-semibold text-ink-900">Nowa karta otworu</h2>
          <button type="button" onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700">
            <Icon name="x" className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 grid gap-4">
          <label className="field">
            <span className="field-label">1. Ujęcie *</span>
            <SearchableSelect value={intakeId === '' ? '' : String(intakeId)}
              onChange={v => setIntakeId(v ? Number(v) : '')}>
              <option value="">— wybierz ujęcie —</option>
              {intakeList.map(it => (
                <option key={it.id} value={it.id}>{it.name} ({it.number})</option>
              ))}
            </SearchableSelect>
            <span className="text-[11px] text-ink-400 mt-1">Każdy otwór należy do ujęcia.</span>
          </label>

          <div>
            <span className="field-label">2. Otwór</span>
            <div className="flex gap-2 mt-1">
              <button type="button" onClick={() => setMode('existing')} disabled={intakeId === ''}
                className={'flex-1 btn btn-sm ' + (mode === 'existing' ? 'btn-primary' : 'btn-secondary') + (intakeId === '' ? ' opacity-50' : '')}>
                <Icon name="layout" className="w-3.5 h-3.5" /> Istniejący
              </button>
              <button type="button" onClick={() => setMode('new')}
                className={'flex-1 btn btn-sm ' + (mode === 'new' ? 'btn-primary' : 'btn-secondary')}>
                <Icon name="plus" className="w-3.5 h-3.5" /> Nowy otwór
              </button>
            </div>

            {mode === 'existing' && (
              <label className="field mt-3">
                <span className="field-label">Wybierz otwór w ujęciu</span>
                <SearchableSelect value={existingId === '' ? '' : String(existingId)}
                  onChange={v => setExistingId(v ? Number(v) : '')}>
                  <option value="">
                    {intakeId === '' ? '— najpierw wybierz ujęcie —'
                      : existingList.length === 0 ? '— brak otworów w ujęciu —' : '— wybierz otwór —'}
                  </option>
                  {existingList.map(o => (
                    <option key={o.id} value={o.id}>{o.designation}</option>
                  ))}
                </SearchableSelect>
              </label>
            )}

            {mode === 'new' && (
              <div className="flex gap-2 mt-3">
                <label className="field flex-[1.3]">
                  <span className="field-label">Oznaczenie *</span>
                  <input value={designation} onChange={e => setDesignation(e.target.value)}
                    className={'input' + (kolidujacy ? ' !border-red-400 bg-red-50' : '')} placeholder="np. 15C" />
                  {kolidujacy && (
                    <button type="button" onClick={() => { setMode('existing'); setExistingId(kolidujacy.id); }}
                      className="mt-1 text-left text-[11px] text-red-600 underline decoration-dotted">
                      Otwór „{kolidujacy.designation}" już jest w tym ujęciu — otwórz jego kartę.
                    </button>
                  )}
                </label>
                <label className="field flex-1">
                  <span className="field-label">Funkcja</span>
                  <SearchableSelect value={func} onChange={setFunc} searchThreshold={20}>
                    {NEW_FUNCTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </SearchableSelect>
                </label>
              </div>
            )}
          </div>

          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}

          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy} className="btn btn-primary">
              <Icon name="arrow" className="w-3.5 h-3.5" /> {busy ? 'Otwieram…' : 'Otwórz edytor karty'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
